import { useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { Button } from "components/DesignSystem/Button/Button";
import { Cross } from "components/icons/Cross";
import { TriangleWarning } from "components/icons/TriangleWarning";
import { getSuppressionReasonDetails } from "constants/emailSuppression";
import { useToast } from "hooks/useToast";
import {
  useGetChannelEmailSuppressionStatusQuery,
  useReactivateChannelEmailSuppressionsMutation,
} from "store/apis/chat";
import { EmailSuppressionStatus } from "types/Models/Chat";
import { pluralize } from "utils/pluralize";
import { ReactivateConfirmModal } from "./ReactivateConfirmModal";
import { SuppressedEmailsModal } from "./SuppressedEmailsModal";

type EmailSuppressionBannerProps = {
  channelId?: string;
  isAdmin: boolean;
};

type PendingConfirm = {
  emails: string[];
  riskyEmails: string[];
};

export const EmailSuppressionBanner = ({
  channelId,
  isAdmin,
}: EmailSuppressionBannerProps) => {
  const [dismissedSuppressionKey, setDismissedSuppressionKey] = useState<
    string | null
  >(null);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [reviewSessionKey, setReviewSessionKey] = useState(0);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(
    null
  );

  const { successToast, alertToast } = useToast();
  const { data: suppressions } = useGetChannelEmailSuppressionStatusQuery(
    isAdmin && channelId ? { channelId } : skipToken,
    { refetchOnMountOrArgChange: true }
  );
  const [reactivateSuppressions, { isLoading: isReactivating }] =
    useReactivateChannelEmailSuppressionsMutation();

  const suppressionKey = suppressions?.length
    ? `${channelId}:${suppressions
        .map(
          (suppression) => `${suppression.email}:${suppression.suppressed_at}`
        )
        .sort()
        .join(",")}`
    : null;

  if (!suppressions?.length || dismissedSuppressionKey === suppressionKey) {
    return null;
  }

  const handleOpenReview = () => {
    setReviewSessionKey((key) => key + 1);
    setIsReviewOpen(true);
  };

  const handleCloseReview = () => {
    setIsReviewOpen(false);
    setPendingConfirm(null);
  };

  const reactivate = async (emails: string[]) => {
    if (!channelId) return;

    try {
      const result = await reactivateSuppressions({
        channelId,
        emails,
      }).unwrap();

      const hasValidShape =
        Array.isArray(result?.reactivated) && Array.isArray(result?.failed);
      if (!hasValidShape) {
        throw new Error("Unexpected reactivate-suppressions response shape");
      }

      if (result.reactivated.length) {
        successToast({
          message: `${pluralize(
            result.reactivated.length,
            "email address",
            "email addresses"
          )} can receive emails again`,
        });
      }
      result.failed.forEach((failure) =>
        alertToast({
          message: failure.message || `Couldn't reactivate ${failure.email}`,
        })
      );

      const stillSuppressed = suppressions.filter(
        (suppression) => !result.reactivated.includes(suppression.email)
      );
      if (stillSuppressed.length === 0) {
        handleCloseReview();
        return;
      }
      setPendingConfirm(null);
    } catch (err) {
      alertToast({
        message:
          (err as { data?: { message?: string } })?.data?.message ??
          "Couldn't reactivate — please try again",
      });
      setPendingConfirm(null);
    }
  };

  const handleReactivateSelected = (selected: EmailSuppressionStatus[]) => {
    const riskySelected = selected.filter(
      (suppression) =>
        getSuppressionReasonDetails(suppression.suppression_reason).isRisky
    );
    if (riskySelected.length) {
      setPendingConfirm({
        emails: selected.map((suppression) => suppression.email),
        riskyEmails: riskySelected.map((suppression) => suppression.email),
      });
      return;
    }
    void reactivate(selected.map((suppression) => suppression.email));
  };

  const handleConfirmReactivate = () => {
    if (!pendingConfirm) return;
    void reactivate(pendingConfirm.emails);
  };

  const handleDismissBanner = () => {
    setDismissedSuppressionKey(suppressionKey);
  };

  const handleCancelConfirm = () => {
    setPendingConfirm(null);
  };

  const emailWord = pluralize(suppressions.length, "email", "emails", false);
  const mailboxWord = pluralize(
    suppressions.length,
    "mailbox",
    "mailboxes",
    false
  );
  const suppressedEmails = suppressions.map((suppression) => suppression.email);
  const suppressionMessage = `We are not able to deliver the ${emailWord} to ${suppressedEmails.join(
    ", "
  )}'s ${mailboxWord}.`;

  return (
    <>
      <div className="t-flex t-items-center t-justify-between t-gap-4 t-bg-orange-0 t-px-4 t-py-2">
        <div className="t-flex t-items-center t-gap-2 t-min-w-0">
          <span className="t-flex t-shrink-0 t-text-orange">
            <TriangleWarning />
          </span>
          <span className="t-text-body-sm t-text-orange-80 t-truncate">
            {suppressionMessage}
          </span>
        </div>
        <div className="t-flex t-items-center t-gap-2 t-shrink-0">
          <Button
            size="xsmall"
            customType="secondary"
            onClick={handleOpenReview}
          >
            Review
          </Button>
          <Button
            customType="ghost_icon"
            size="xsmall"
            onClick={handleDismissBanner}
          >
            <Cross color="currentColor" />
          </Button>
        </div>
      </div>

      <SuppressedEmailsModal
        key={reviewSessionKey}
        open={isReviewOpen && !pendingConfirm}
        suppressions={suppressions}
        isReactivating={isReactivating}
        onClose={handleCloseReview}
        onReactivate={handleReactivateSelected}
      />
      <ReactivateConfirmModal
        open={!!pendingConfirm}
        riskyEmails={pendingConfirm?.riskyEmails ?? []}
        isLoading={isReactivating}
        onCancel={handleCancelConfirm}
        onConfirm={handleConfirmReactivate}
      />
    </>
  );
};
